if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('#QuickStandardModal');
      this.productCache = new Map();
      this.pendingRequests = new Map();
      this.activeRequestId = 0;
      this.prefetchInitialized = false;
      this.prefetchHandlers = null;
      this.prefetchObserver = null;

      this.addEventListener('product-info:loaded', ({ target }) => {
        target.addPreProcessCallback(this.preprocessHTML.bind(this));
      });
    }

    connectedCallback() {
      super.connectedCallback();
      this.setupPrefetch();
    }

    hide(preventFocus = false) {
      this.activeRequestId += 1;

      const cartNotification = document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      setTimeout(() => {
        if (!this.hasAttribute('open')) {
          this.modalContent.innerHTML = '';
          this.modalContent.classList.remove('is-loading');
        }
      }, 500);

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      const productUrl = opener.getAttribute('data-product-url')?.split('?')[0];
      if (!productUrl) {
        super.show(opener);
        return;
      }

      const requestId = ++this.activeRequestId;

      this.toggleOpenerLoading(opener, true);
      this.renderLoadingState();
      super.show(opener);

      this.fetchProductMarkup(productUrl)
        .then((productMarkup) => {
          this.renderProductMarkup(productMarkup, requestId);
        })
        .catch((error) => {
          if (requestId !== this.activeRequestId) return;

          this.modalContent.classList.remove('is-loading');
          this.modalContent.innerHTML = `
            <div class="quick-add-modal__status" role="status" aria-live="polite">
              <p>Unable to load product options. Please try again.</p>
            </div>
          `;
        })
        .finally(() => {
          if (requestId === this.activeRequestId) {
            this.toggleOpenerLoading(opener, false);
          }
        });

    }

    setupPrefetch() {
      if (this.prefetchInitialized) return;
      this.prefetchInitialized = true;

      const prefetchFromEvent = (event) => {
        const button = event.target.closest('.quick-add__submit[data-product-url]');
        if (!button) return;

        this.prefetchProduct(button.getAttribute('data-product-url'));
      };

      this.prefetchHandlers = {
        pointerenter: prefetchFromEvent,
        focusin: prefetchFromEvent,
        touchstart: prefetchFromEvent,
      };

      document.addEventListener('pointerenter', prefetchFromEvent, true);
      document.addEventListener('focusin', prefetchFromEvent, true);
      document.addEventListener('touchstart', prefetchFromEvent, { capture: true, passive: true });

      if ('IntersectionObserver' in window) {
        this.prefetchObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;

              const button = entry.target;
              observer.unobserve(button);
              this.prefetchProduct(button.getAttribute('data-product-url'));
            });
          },
          { rootMargin: '300px 0px' }
        );

        document.querySelectorAll('.quick-add__submit[data-product-url]').forEach((button) => {
          this.prefetchObserver.observe(button);
        });
      }
    }

    prefetchProduct(productUrl) {
      const normalizedProductUrl = productUrl?.split('?')[0];
      if (!normalizedProductUrl || this.productCache.has(normalizedProductUrl) || this.pendingRequests.has(normalizedProductUrl)) {
        return;
      }

      this.fetchProductMarkup(normalizedProductUrl).catch(() => {});
    }

    fetchProductMarkup(productUrl) {
      const normalizedProductUrl = productUrl?.split('?')[0];
      if (!normalizedProductUrl) {
        return Promise.reject(new Error('Quick add product URL is missing.'));
      }

      if (this.productCache.has(normalizedProductUrl)) {
        return Promise.resolve(this.productCache.get(normalizedProductUrl));
      }

      if (this.pendingRequests.has(normalizedProductUrl)) {
        return this.pendingRequests.get(normalizedProductUrl);
      }

      const request = fetch(`${normalizedProductUrl}?view=quick_add`)
        .then((response) => response.text())
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const productElement = responseHTML.querySelector('product-info');
          if (!productElement) throw new Error('Quick add product info was not found.');

          this.preprocessHTML(productElement);
          const productMarkup = productElement.outerHTML;
          this.productCache.set(normalizedProductUrl, productMarkup);
          return productMarkup;
        })
        .finally(() => {
          this.pendingRequests.delete(normalizedProductUrl);
        });

      this.pendingRequests.set(normalizedProductUrl, request);
      return request;
    }

    toggleOpenerLoading(opener, isLoading) {
      if (!opener) return;

      const spinner = opener.querySelector('.loading__spinner');
      opener.toggleAttribute('aria-disabled', isLoading);
      opener.classList.toggle('loading', isLoading);
      if (spinner) spinner.classList.toggle('hidden', !isLoading);
    }

    renderLoadingState() {
      this.modalContent.classList.add('is-loading');
      this.modalContent.innerHTML = `
        <div class="quick-add-modal__status" role="status" aria-live="polite">
          <div class="quick-add-modal__spinner">
            <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
              <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
            </svg>
          </div>
        </div>
      `;
    }

    renderProductMarkup(productMarkup, requestId) {
      if (requestId !== this.activeRequestId) return;

      this.modalContent.classList.remove('is-loading');
      HTMLUpdateUtility.setInnerHTML(this.modalContent, productMarkup);
      if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
      if (window.ProductModel) window.ProductModel.loadShopifyXR();
    }

    preprocessHTML(productElement) {
      productElement.classList.forEach((classApplied) => {
        if (classApplied.startsWith('color-') || classApplied === 'gradient')
          this.modalContent.classList.add(classApplied);
      });
      this.preventDuplicatedIDs(productElement);
      this.removeDOMElements(productElement);
      this.removeGalleryListSemantic(productElement);
      this.updateImageSizes(productElement);
      this.preventVariantURLSwitching(productElement);
    }

    preventVariantURLSwitching(productElement) {
      productElement.setAttribute('data-update-url', 'false');
    }

    removeDOMElements(productElement) {
      const pickupAvailability = productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const shareButton = productElement.querySelector('share-button');
      if (shareButton) shareButton.remove();

      const productModal = productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const modalDialog = productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach((modal) => modal.remove());

      const sideDrawerOpener = productElement.querySelectorAll('side-drawer-opener');
      if (sideDrawerOpener) {
        sideDrawerOpener.forEach((button) => {
          if (!button.classList.contains('product-popup-modal__opener--keep')) button.remove()
        })
      };

      const sideDrawer = productElement.querySelectorAll('side-drawer');
      if (sideDrawer) {
        sideDrawer.forEach((drawer) => {
           if (!drawer.classList.contains('product-popup-modal__drawer--keep')) drawer.remove()
        })
      };
    }

    preventDuplicatedIDs(productElement) {
      const sectionId = productElement.dataset.section;

      const oldId = sectionId;
      const newId = `quickadd-${sectionId}`;
      productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
      Array.from(productElement.attributes).forEach((attribute) => {
        if (attribute.value.includes(oldId)) {
          productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
        }
      });

      productElement.dataset.originalSection = sectionId;
    }

    removeGalleryListSemantic(productElement) {
      const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes(productElement) {
      const product = productElement.querySelector('.product');
      const desktopColumns = product?.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes =
        '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
    }
  });
}
